/* @flow */
import React from 'react';
import { timeFormat } from 'd3-time-format';
import { scaleTime, scaleLinear } from 'd3-scale';
import { histogram, max } from 'd3-array';
import classnames from 'classnames/bind';
import createDebugger from 'debug';

import s from './TimelineSlider.styl';
const cx = classnames.bind(s);
const debug = createDebugger('lib:components:TimelineSlider'); // eslint-disable-line no-unused-vars
const formatDate = timeFormat('%m/%d/%Y');

type TimelineSliderProps = {
  extent: [number, number], // Full extent
  value: [number, number], // Current value / selection on the timeline slider
  onStart: (extent: [number, number]) => void,
  onChange: (extent: [number, number]) => void,
  onEnd: (extent: [number, number]) => void,
  width: number,
  height: number,
  margin: number,
  className?: string,
  nodeTimestamps: number[],
};

const histogramHeight = 50;

/**
 * Create a new xScale for mapping between the selected time range and the width
 * of the slider in pixels.
 *
 * TODO: This is almost exactly the same scale used for the histogram utils. In
 * fact it likely should be the same scale, however this doesn't use nice() (for
 * now)
 */
const createScale = (props: TimelineSliderProps): D3$TimeScale => {
  const { extent, width, margin } = props;
  return scaleTime()
    .domain(extent)
    .range([0, width + margin]);
};

/**
 * NOTE: Nice-ing without a count in this case can often cause the domain to be
 * extended. This makes the timeline slider histogram longer in terms of domain
 * than the actual data represented by the graph, and since the actual slider
 * component of the timeline slider is not actually bound to the visual
 * histogram in any way (they are just derived from the same data) we want the
 * rendered histogram to match up as closely as possible with the rendered
 * slider. The underlying data may be the same but once rendered to pixels
 * differences can occur. Standardizing tick count minimizes that.
 */
const createHistogramUtils = (props: TimelineSliderProps) => {
  const { extent, width, margin, nodeTimestamps } = props;
  const tickCount = 100;
  const histogramWidth = width + margin;

  const xScale = scaleTime()
    .domain(extent)
    .range([0, histogramWidth])
    .nice(tickCount); // Must nice with a count. See NOTE

  const createBins = histogram()
    .domain(xScale.domain())
    .thresholds(xScale.ticks(tickCount));

  const bins = createBins(nodeTimestamps);

  const yScale = scaleLinear()
    .domain([0, max(bins, x => x.length)])
    .range([histogramHeight, 0]);

  return {
    bins,
    xScale,
    yScale,
    width: histogramWidth,
  };
};

export class TimelineSlider extends React.Component {

  static defaultProps = {
    height: 30,
    width: 0,
    margin: 10,
  };

  props: TimelineSliderProps;

  handles: D3$Selection;

  slideArea: ?Element;

  xScale: D3$TimeScale = createScale(this.props);

  histogram = createHistogramUtils(this.props);

  dragDirection: 'east' | 'west' = 'east';

  isDragging: boolean = false;

  dragStart: ?number = null;

  mouseHandleOffset: number = 0;

  getDirectionalPosition = (e: SyntheticMouseEvent) => {
    if (this.dragStart === null) return null;

    return {
      direction: this.dragDirection,
      offset: e.pageX - (this.dragStart || 0),
    };
  };

  /**
   * Get the max value for the scale. Just wrapped since it uses ugly indexing.
   */
  getScaleMax = () => this.xScale.range()[1];

  /**
   * Convert the controlled value prop to pixels rather than timestamps
   */
  // Flow is having trouble here since it sees [number,number] as a
  // touble type but [].map returns an array type.
  getPositionValue = () => {
    return this.props.value.map(this.xScale);
  };

  /**
   * The heavy lifting of determining position. Usually it is just startPos +
   * offset (offset can be negative). However, we also account for a few addition
   * validations:
   *
   * - Dragging the whole bar, which means updating east and west in tandem
   * - Dragging one of the handles up against the other. I.e. handling dragging
   *   overflow
   * - Dragging handles against the edges of the slide area. We don't want them
   *   to go outside the area
   */
  updatePosition = (e: SyntheticMouseEvent, cb: Function) => {
    const _pos = this.getDirectionalPosition(e);

    if (!_pos) return;

    let { direction, offset } = _pos;
    const max = this.getScaleMax();
    const nextPos = { ...this.startPos };

    // Current values
    const [ currentWest, currentEast ] = this.getPositionValue();

    // Dragging the whole bar, not just one handle
    if (direction === 'both') {
      const width = this.startPos.east - this.startPos.west;
      let nextEast = this.startPos.east + offset;
      let nextWest = this.startPos.west + offset;

      // Don't allow dragging the bar past the edges
      if (nextEast > max) {
        nextEast = max;
        nextWest = nextEast - width;
      } else if (nextWest < 0) {
        nextWest = 0;
        nextEast = nextWest + width;
      }

      nextPos.east = nextEast;
      nextPos.west = nextWest;
    } else { // Dragging a single handle
      let pos = this.startPos[direction] + offset;

      if (pos < 0) {
        pos = 0;
      } else if (pos > max) {
        pos = max;
      }

      // NOTE: This is where we would do the flip flop of direction if we were
      // going to support that feature. I haven't included it here in the hopes
      // of reducing overal complexity, since all this positioning logic is
      // already fairly difficult to follow.
      if (direction === 'west' && pos > currentEast) {
        pos = currentEast;
      } else if (direction === 'east' && pos < currentWest) {
        pos = currentWest;
      }

      nextPos[direction] = pos;
    }

    // We need to map from pixel values to timestamps
    const changeValue = [nextPos.west, nextPos.east]
      .map(this.xScale.invert)
      .map(date => date.getTime());

    // Emit the change. cb is passsed rather than simply calling
    // this.props.onChange since it is also used for the onEnd callback
    cb(changeValue);
  };

  startPos: Object = {};

  handleDragStart = (e: SyntheticMouseEvent) => {
    e.preventDefault();
    const handle = e.target;
    // $FlowIssue: Flow is so bad with EventTarget
    const ew = handle.dataset.direction;
    // $FlowIssue
    const rect = handle.getBoundingClientRect();
    const x = e.clientX;
    const [ west, east ] = this.getPositionValue();

    // This is best drawn as apicture, but in words: We need the difference
    // between the left or right edge of the handle and the coordinate of the
    // mouse. Otherwise clicking within the handle will cause its edge to jump
    // over to the mouse, since we're not accounting for that small distance of
    // pixels from the mouse to the edge of the handle.
    this.mouseHandleOffset = x - (ew === 'east' ? rect.right : rect.left);

    // Since we're using left/right for handle positioning we need to
    // distinguish which handle was dragged
    this.dragDirection = ew;

    // Start the drag. The mousemove listener is constantly running so this flag
    // will allow it to short circuit when we're not actually dragging.
    this.isDragging = true;

    // The start position is stored so that we can calculate the difference
    // between this and where the mouse is during and after a drag
    this.dragStart = x;

    this.startPos = { west, east };

    if (this.props.onStart) {
      // $FlowIgnore
      this.props.onStart(this.getPositionValue());
    }
  };

  handleDrag = (e: SyntheticMouseEvent) => {
    if (!this.isDragging) {
      return;
    }

    this.updatePosition(e, this.props.onChange);
  };

  handleDragStop = (e: SyntheticMouseEvent) => {
    this.updatePosition(e, this.props.onEnd);
    this.isDragging = false;
    this.dragStart = null;
  };

  render() {
    // Determine whether the two tooltips are overlapping. NOTE: As stated in
    // the TODO below we do not measure the dom. This number was simply taken
    // from the width of the tooltip at the time of writing.
    const tooltipWidth = 110; // TODO: Refactor to measure DOM and make this reliable
    const [ west, east ] = this.getPositionValue();
    const overlapping = Math.abs(west - east) <= (tooltipWidth + 10); // The +10 is to provide a small buffer
    const { histogram } = this;

    // NOTE: To get the pixel value for `right` we need the max value minus the
    // east value. For example, if our total pixel range is 0-800 and east is
    // 800 that means the right handle should be all the way at the end, meaning
    // right: 0.
    //
    // NOTE: mouseleave is important, NOT mouseout. mouseOUT will fire events
    // when the mouse moves over elements _within_ the div. So you get mouseout
    // events while dragging, which just won't do. The mouseLEAVE event fires
    // only when the mouse leaves the bounding box of this div, regardless of
    // what heppens within. This is what we want.
    //
    // Admitedly it would be nice if the dragging did not stop if the mouse was
    // still down, but to get that functionality we need a document-level
    // mouseup listener. We can do that, but let's leave it for a future rev.
    // Hopefully this will only be a minor inconvenience, if even that.
    return (
      <div
        data-selector='GraphControlsTimelineSlider'
        onMouseMove={this.handleDrag}
        onMouseUp={this.handleDragStop}
        onMouseLeave={this.handleDragStop/* See NOTE */}
        style={{ height: this.props.height }}
        className={cx('TimelineSlider', this.props.className)}>

        <div ref={el => { this.slideArea = el; }} className={cx('slideArea')}>
          <div style={{ left: Math.max(0, west), right: this.getScaleMax() - east /* See NOTE */ }} className={cx('slider')}>
            <div className={cx('handle', 'w')} data-direction='west' onMouseDown={this.handleDragStart} />
            <div className={cx('body')} data-direction='both' onMouseDown={this.handleDragStart}>
              <DateTooltip
                key='w'
                className={cx('DateTooltip', 'w', {
                  invertY: overlapping,
                })}
                pos={0}
                date={this.xScale.invert(west)}
              />
              <DateTooltip
                key='e'
                className={cx('DateTooltip', 'e')}
                pos={'auto'}
                date={this.xScale.invert(east)}
              />
            </div>
            <div className={cx('handle', 'e')} data-direction='east' onMouseDown={this.handleDragStart} />
          </div>
        </div>

        <div className={cx('histogram')} style={{ width: histogram.width, height: histogramHeight }}>
          {histogram.bins.map((d, i) => {
            const columnSpacing = 1;
            const marginWidth = (histogram.bins.length - 1) * columnSpacing;
            return (
              <div
                key={i}
                className={cx('bin')}
                style={{
                  left: histogram.xScale(d.x0) + columnSpacing,
                  top: histogram.yScale(d.length),
                  // width: (this.props.width - marginWidth) / histogram.bins.length,
                  // width: (histogram.width - marginWidth) / histogram.bins.length,
                  width: Math.max(0, histogram.xScale(d.x1) - histogram.xScale(d.x0) - 1),
                  height: histogram.yScale(0) - histogram.yScale(d.length),
                }}
              />
            );
          })}
        </div>

        <div className={cx('extentSummary')}>
          {this.props.extent
            .map(x => new Date(x))
            .map(formatDate)
            .join(' to ')}
        </div>
      </div>
    );
  }
}

type DateTooltipProps = {
  className?: string,
  pos: number | 'auto',
  date: Date,
};

class DateTooltip extends React.Component {
  props: DateTooltipProps;

  render() {
    const { date, pos } = this.props;
    return (
      <div
        style={{ left: pos }}
        className={this.props.className}>
        <span className={cx('date')}>{formatDate(date)}</span>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./web_modules/trustar/components/TimelineSlider.js